import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Images } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const pub = (p) => (p ? `${window.location.origin}/api/public/img/${p}` : null);

export default function AdminGallery() {
  const [items, setItems] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/public/content")
      .then((r) => setItems(r.data.gallery || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choisissez une photo à ajouter.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("file", f);
      await api.post("/admin/gallery", fd);
      toast.success("Photo ajoutée à la galerie du site.");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Retirer cette photo de la galerie ?")) return;
    try {
      await api.delete(`/admin/gallery/${it.id}`);
      toast.success("Photo retirée.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-gallery-view">
      <h2 className="flex items-center gap-2 font-display text-xl font-bold text-korban-navy">
        <Images size={19} className="text-korban-blue" /> Galerie photos du site
      </h2>
      <form onSubmit={upload} data-testid="gallery-form" className="mt-4 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="min-w-[180px] flex-1">
          <label htmlFor="g-title" className="mb-1 block text-xs font-bold text-slate-600">Légende (facultatif)</label>
          <input id="g-title" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex. Formation GRH — octobre 2026" data-testid="gallery-input-title" className="field field-light" />
        </div>
        <div>
          <label htmlFor="g-file" className="mb-1 block text-xs font-bold text-slate-600">Photo</label>
          <input id="g-file" type="file" ref={fileRef} accept=".jpg,.jpeg,.png,.webp,.gif"
            data-testid="gallery-input-file"
            className="block text-xs text-slate-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-korban-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-korban-gold" />
        </div>
        <button type="submit" disabled={busy} data-testid="gallery-add-button"
          className="inline-flex items-center gap-2 rounded-lg bg-korban-navy px-5 py-3 text-sm font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Ajouter
        </button>
      </form>

      {!items ? (
        <p className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="gallery-admin-empty">
          La galerie est vide : ajoutez des photos pour les afficher sur le site.
        </p>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((it) => (
            <div key={it.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`gallery-item-${it.id}`}>
              <img src={pub(it.path)} alt={it.title || "Photo de la galerie"} className="h-28 w-full object-cover" />
              <div className="flex items-center justify-between gap-2 p-2.5">
                <p className="truncate text-xs font-semibold text-slate-600">{it.title || "Sans légende"}</p>
                <button onClick={() => remove(it)} data-testid={`gallery-delete-${it.id}`}
                  className="shrink-0 rounded-lg bg-white px-2.5 py-1.5 text-xs font-bold text-red-600 ring-1 ring-red-200 hover:bg-red-50">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
